import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, withCtx as _withCtx, mergeProps as _mergeProps, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 0 }

import RichTextField from '@/components/common/RichTextField.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import {
  EventItem,
  repeatOptions,
  titleRules,
  descriptionRules,
  defaultEventItem,
} from '@/models/event-item.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { EventsActions, EventsState } from '@/stores/events.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { format } from 'date-fns';
import CategoryField from '../categories/CategoryField.vue';
import AddressField from '../common/AddressField.vue';
import CategoriesField from '../categories/CategoriesField.vue';
import { impactAreaItems } from '@/models/initiative.model';
import { useI18n } from 'vue-i18n';


export default /*@__PURE__*/_defineComponent({
  __name: 'EventItemForm',
  props: {
  data: {
    type: Object as PropType<EventItem>,
  },
},
  setup(__props) {

const store = useStore<ShiftCMSState & { ev: EventsState }>();
const { t } = useI18n();

const events = useEvents();

const props = __props;

const valid = ref<boolean>(false);

const eventItem = ref<EventItem>({ ...defaultEventItem });
let currentValue = ref<EventItem>({ ...defaultEventItem });

const action = computed<string>(() =>
  eventItem.value.id === NEW_ID ? 'Add' : 'Save',
);

const save = () => saveEventItem(null, false);

const saveEventItem = async (e: unknown, close = true) => {
  let data: { status: boolean; data: EventItem | null } = {
    status: false,
    data: null,
  };
  if (valid.value) {
    if (eventItem.value.id === NEW_ID) {
      data = await store.dispatch(EventsActions.AddEventItem, eventItem.value);
    } else {
      data = await store.dispatch(
        EventsActions.UpdateEventItem,
        eventItem.value,
      );
    }

    Object.assign(eventItem.value, { ...data.data });
  }
  if (data.status) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved EventItem',
      type: 'success',
    });

    if (close) {
      store.commit(Mutations.HideDrawer);
      store.dispatch(EventsActions.GetEventItems);
    }
    currentValue.value = { ...eventItem.value };
    store.commit(Mutations.SetDirtyState, false);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Learning Item',
      type: 'error',
    });
  }
};

const updateLanguage = async () => {
  if (eventItem.value.id === NEW_ID) {
    Object.assign(eventItem, { title: '' });
  } else {
    const item = await store.dispatch(
      EventsActions.GetEventItem,
      eventItem.value.id,
    );

    if (item) {
      Object.assign(eventItem.value, {
        ...item,
        description: item.description || '',
        startDate: new Date(item.startDate || new Date().toISOString()),
        endDate: new Date(item.endDate || new Date().toISOString()),
      });
      currentValue.value = { ...eventItem.value };
    }
  }
};

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);

  if (props.data) {
    Object.assign(eventItem.value, {
      ...props.data,
      description: props.data.description || '',
      startDate: new Date(props.data.startDate || new Date().toISOString()),
      endDate: new Date(props.data.endDate || new Date().toISOString()),
    });

    currentValue.value = { ...eventItem.value };
  } else {
    Object.assign(eventItem.value, { ...defaultEventItem });

    currentValue.value = { ...defaultEventItem };
  }
});

const removeLabel = async () => {
  if (
    eventItem.value &&
    eventItem.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Event Item"?')
  ) {
    store.dispatch(EventsActions.DeleteEventItem, eventItem.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

const togglePublished = async (published: boolean) => {
  if (eventItem.value.id !== -1) {
    store.dispatch(EventsActions.PublishEventItem, {
      id: eventItem.value.id,
      published,
    });
  }
  eventItem.value.published = published;
};
const toggleDismissed = async (dismissed: boolean) => {
  if (eventItem.value.id !== -1) {
    store.dispatch(EventsActions.DismissEventItem, {
      id: eventItem.value.id,
      dismissed,
    });
  }
  eventItem.value.dismissed = dismissed;
};

function checkUrl(url: string) {
  window.open(url, '_new');
}

const formatDate = (v: string): string => {
  return format(new Date(v), 'dd-MM-yyyy HH:mm');
};

const startDate = computed(
  () => new Date(eventItem.value.startDate || new Date()),
);
const endDate = computed(() => new Date(eventItem.value.endDate || new Date()));

const startTime = computed(() =>
  format(new Date(eventItem.value.startDate || new Date()), 'HH:mm'),
);
const endTime = computed(() =>
  format(new Date(eventItem.value.endDate || new Date()), 'HH:mm'),
);

const updateStartTime = (v: string | null) => {
  const d = new Date(eventItem.value.startDate || new Date());
  eventItem.value.startDate = new Date(
    format(d, `yyyy-MM-dd ${v}`),
  ).toISOString();
};
const updateEndTime = (v: string | null) => {
  const d = new Date(eventItem.value.endDate || new Date());
  eventItem.value.endDate = new Date(
    format(d, `yyyy-MM-dd ${v}`),
  ).toISOString();
};

const updateStartDate = (v: Date) => {
  eventItem.value.startDate = new Date(
    format(new Date(v || new Date()), `yyyy-MM-dd ${startTime.value}`),
  ).toISOString();
};
const updateEndDate = (v: Date) => {
  eventItem.value.endDate = new Date(
    format(new Date(v || new Date()), `yyyy-MM-dd ${endTime.value}`),
  ).toISOString();
};

function getSimpleValue(i: EventItem) {
  return {
    title: i.title,
    description: i.description,
    url: i.url,
    imageUrl: i.imageUrl,
    startDate: i.startDate,
    endDate: i.endDate,
    categories: i.categories.map((c) => c.id),
    address: i.address,
    location: i.location,
  };
}

const isDirty = computed<boolean>(() => {
  const isDirty =
    JSON.stringify(getSimpleValue(eventItem.value)) !==
    JSON.stringify(getSimpleValue(currentValue.value));

  store.commit(Mutations.SetDirtyState, isDirty);

  return isDirty;
});

let translatedAreaItems = impactAreaItems.map((i) => ({
  title: t(`initiative.edit.area_${i.value}`),
  value: i.value,
}));

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_label = _resolveComponent("v-label")!
  const _component_v_date_picker = _resolveComponent("v-date-picker")!
  const _component_v_time_picker = _resolveComponent("v-time-picker")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    modelValue: valid.value,
    "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((valid).value = $event)),
    onSubmit: _withModifiers(saveEventItem, ["prevent"])
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_container, null, {
        default: _withCtx(() => [
          _createElementVNode("h2", null, [
            _createTextVNode(_toDisplayString(_ctx.$t(action.value)) + " an event item ", 1),
            (isDirty.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_1, "*"))
              : _createCommentVNode("", true),
            _createVNode(CurrentLanguage)
          ]),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "8" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: eventItem.value.title,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((eventItem.value.title) = $event)),
                    rules: _unref(titleRules),
                    label: "Title",
                    hint: "Not used on eventItem page. Use a top level header in the description",
                    required: ""
                  }, null, 8, ["modelValue", "rules"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "2",
                class: "pt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "mt-2",
                    block: "",
                    disabled: eventItem.value.id === _unref(NEW_ID),
                    onClick: _cache[1] || (_cache[1] = () => togglePublished(!eventItem.value.published)),
                    color: !eventItem.value.published ? 'success' : 'warning'
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(eventItem.value.published ? 'Un-publish' : 'Publish'), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled", "color"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "2",
                class: "pt-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "mt-2",
                    block: "",
                    disabled: eventItem.value.id === _unref(NEW_ID),
                    onClick: _cache[2] || (_cache[2] = () => toggleDismissed(!eventItem.value.dismissed)),
                    color: !eventItem.value.dismissed ? 'warning' : 'error'
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(eventItem.value.dismissed ? 'Un-dismiss' : 'Dismiss'), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled", "color"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(CategoryField, {
            main: "",
            disabled: "",
            modelValue: eventItem.value.mainCategory,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((eventItem.value.mainCategory) = $event)),
            label: "Main Category"
          }, null, 8, ["modelValue"]),
          _createVNode(CategoriesField, {
            modelValue: eventItem.value.categories,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((eventItem.value.categories) = $event))
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "9" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: eventItem.value.url,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((eventItem.value.url) = $event)),
                    label: "URL",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, { class: "pt-6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    block: "",
                    color: "info",
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (checkUrl(eventItem.value.url))),
                    disabled: eventItem.value.url == ''
                  }, {
                    default: _withCtx(() => _cache[19] || (_cache[19] = [
                      _createTextVNode("Check")
                    ])),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "9" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: eventItem.value.imageUrl,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((eventItem.value.imageUrl) = $event)),
                    label: "Image URL",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, { class: "pt-6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    block: "",
                    color: "info",
                    onClick: _cache[8] || (_cache[8] = ($event: any) => (checkUrl(eventItem.value.imageUrl))),
                    disabled: eventItem.value.imageUrl == ''
                  }, {
                    default: _withCtx(() => _cache[20] || (_cache[20] = [
                      _createTextVNode("Check")
                    ])),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_text_field, {
            modelValue: eventItem.value.location,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((eventItem.value.location) = $event)),
            label: "Location",
            required: ""
          }, null, 8, ["modelValue"]),
          _createVNode(AddressField, {
            modelValue: eventItem.value.address,
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((eventItem.value.address) = $event))
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_select, {
            modelValue: eventItem.value.impactArea,
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((eventItem.value.impactArea) = $event)),
            items: _unref(translatedAreaItems),
            label: _ctx.$t('initiative.edit.area'),
            hint: _ctx.$t('initiative.edit.areaHint'),
            required: ""
          }, null, 8, ["modelValue", "items", "label", "hint"]),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_dialog, { width: "auto" }, {
                    activator: _withCtx(({ props: activatorProps }) => [
                      _createVNode(_component_v_label, null, {
                        default: _withCtx(() => _cache[21] || (_cache[21] = [
                          _createTextVNode("Start")
                        ])),
                        _: 1
                      }),
                      _createVNode(_component_v_btn, _mergeProps(activatorProps, {
                        class: "mt-2",
                        type: "button",
                        block: "",
                        flat: "",
                        outline: "",
                        color: "info"
                      }), {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(formatDate(eventItem.value.startDate || new Date().toISOString())), 1)
                        ]),
                        _: 2
                      }, 1040)
                    ]),
                    default: _withCtx(({ isActive }) => [
                      _createVNode(_component_v_card, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_text, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_row, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_col, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_date_picker, {
                                        modelValue: startDate.value,
                                        "onUpdate:modelValue": [
                                          _cache[12] || (_cache[12] = ($event: any) => ((startDate).value = $event)),
                                          updateStartDate
                                        ],
                                        label: "Date"
                                      }, null, 8, ["modelValue"])
                                    ]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_col, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_time_picker, {
                                        modelValue: startTime.value,
                                        "onUpdate:modelValue": [
                                          _cache[13] || (_cache[13] = ($event: any) => ((startTime).value = $event)),
                                          updateStartTime
                                        ],
                                        format: "24hr",
                                        scrollable: "",
                                        label: "Time"
                                      }, null, 8, ["modelValue"])
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_btn, {
                                color: "info",
                                onClick: ($event: any) => (isActive.value = false)
                              }, {
                                default: _withCtx(() => _cache[22] || (_cache[22] = [
                                  _createTextVNode("Close")
                                ])),
                                _: 2
                              }, 1032, ["onClick"])
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, { cols: "4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_dialog, { width: "auto" }, {
                    activator: _withCtx(({ props: activatorProps }) => [
                      _createVNode(_component_v_label, null, {
                        default: _withCtx(() => _cache[23] || (_cache[23] = [
                          _createTextVNode("End")
                        ])),
                        _: 1
                      }),
                      _createVNode(_component_v_btn, _mergeProps(activatorProps, {
                        class: "mt-2",
                        type: "button",
                        block: "",
                        flat: "",
                        outline: "",
                        color: "info"
                      }), {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(formatDate(eventItem.value.endDate || new Date().toISOString())), 1)
                        ]),
                        _: 2
                      }, 1040)
                    ]),
                    default: _withCtx(({}) => [
                      _createVNode(_component_v_card, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_text, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_row, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_col, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_date_picker, {
                                        modelValue: endDate.value,
                                        "onUpdate:modelValue": [
                                          _cache[14] || (_cache[14] = ($event: any) => ((endDate).value = $event)),
                                          updateEndDate
                                        ],
                                        label: "Date"
                                      }, null, 8, ["modelValue"])
                                    ]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_col, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_time_picker, {
                                        modelValue: endTime.value,
                                        "onUpdate:modelValue": [
                                          _cache[15] || (_cache[15] = ($event: any) => ((endTime).value = $event)),
                                          updateEndTime
                                        ],
                                        format: "24hr",
                                        scrollable: "",
                                        label: "Time"
                                      }, null, 8, ["modelValue"])
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, { cols: "4" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_select, {
                    modelValue: eventItem.value.repeats,
                    "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((eventItem.value.repeats) = $event)),
                    label: "Repeats",
                    items: _unref(repeatOptions),
                    required: ""
                  }, null, 8, ["modelValue", "items"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(RichTextField, {
            label: "Description",
            height: 200,
            rules: _unref(descriptionRules),
            modelValue: eventItem.value.description,
            "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((eventItem.value.description) = $event)),
            media: []
          }, null, 8, ["rules", "modelValue"]),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "mt-2",
                    type: "button",
                    color: "warning",
                    onClick: removeLabel,
                    block: ""
                  }, {
                    default: _withCtx(() => _cache[24] || (_cache[24] = [
                      _createTextVNode("Delete")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "mt-2",
                    type: "reset",
                    block: ""
                  }, {
                    default: _withCtx(() => _cache[25] || (_cache[25] = [
                      _createTextVNode("Reset")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "mt-2",
                    type: "button",
                    onClick: save,
                    color: "success",
                    block: ""
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(action.value) + " ", 1),
                      (isDirty.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "mt-2",
                    type: "submit",
                    color: "success",
                    block: ""
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(action.value) + " and close ", 1),
                      (isDirty.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})